<template>
  <div v-clickoutside="handleClose" class="due-date-picker">
    <!-- BUTTON -->
    <div v-if="slotHydrated" class="due-date-picker__button-slot" @click="toggleCalendar">
      <slot name="button" />
    </div>
    <dd-button
      v-if="!slotHydrated"
      :is-disabled="isDisabled"
      :class="{ 'due-date-picker__selector--selected': hasSelectedDate }"
      :title="buttonTitle"
      :size="size"
      type="flat"
      class="due-date-picker__selector"
      @click="toggleCalendar"
    >
      <i v-if="!textToDisplay" class="ddi ddi-calendar" />
      <span v-html="textToDisplay" />
      <i class="ddi ddi-caret-down" />
    </dd-button>
    <!-- PICKER (TABLET/DESKTOP) -->
    <div v-if="$screen.isTabletOrDesktop($mq) && calendarVisible" class="due-date-picker__dropdown">
      <el-date-picker-panel
        :model-value="pickerDate"
        :border="false"
        type="date"
        @update:model-value="handlePick"
      />
      <div class="due-date-picker__footer">
        <button type="button" class="clear-button" @click="handleClear">{{ clearButtonText }}</button>
      </div>
    </div>
    <!-- PICKER (MOBILE) -->
    <action-sheet
      :visible="calendarVisible"
      :title="sheetTitle"
      @close="calendarVisible = false"
    >
      <template #list>
        <div v-if="$screen.isMobile($mq) && calendarVisible" class="due-date-picker__sheet">
          <el-date-picker-panel
            :model-value="pickerDate"
            :border="false"
            type="date"
            @update:model-value="handlePick"
          />
          <div class="due-date-picker__footer">
            <button type="button" class="clear-button" @click="handleClear">{{ clearButtonText }}</button>
          </div>
        </div>
      </template>
    </action-sheet>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import dayjs from '@/helpers/dayjs'
import { ClickOutside as Clickoutside } from 'element-plus'

import ActionSheet from '@/components/global/action-sheet.vue'
import DdButton from '@/components/global/buttons/dd-button.vue'

/**
 * Due date picker component
 */

export default defineComponent({
  name: 'DueDatePicker',

  directives: { Clickoutside },

  components: {
    ActionSheet,
    DdButton,
  },

  props: {
    /* A YYYY-MM-DD date (or anything dayjs reads) */
    modelValue: {
      type: String,
      default: null,
    },
    /* By default, hitting "Clear due date" changes the value to null.
       Instead, this will emit the event 'clear-due-date-clicked' */
    emitEventOnClear: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String,
      default: 'small',
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
    /* Title of the mobile action sheet. Lets the picker be reused for dates other than the due date. */
    sheetTitle: {
      type: String,
      default: 'Due Date',
    },
    /* Text of the clear button in the calendar footer */
    clearButtonText: {
      type: String,
      default: 'Clear due date',
    },
    /* Prefix used in the button tooltip when a date is selected */
    buttonTitlePrefix: {
      type: String,
      default: 'Due',
    },
  },

  emits: ['clear-due-date-clicked', 'update:modelValue'],

  data() {
    return {
      calendarVisible: false,
    }
  },

  computed: {
    // A "YYYY-MM-DD" string read with new Date() is UTC midnight, which is the day before west of UTC.
    // Give the calendar the local date instead, as dayjs reads it for the button text.
    pickerDate(): Date | null {
      return this.modelValue ? dayjs(this.modelValue).toDate() : null
    },
    formattedDate(): string {
      return this.modelValue
        ? dayjs(this.modelValue).format(this.getFormattedDate(this.modelValue))
        : ''
    },
    slotHydrated(): boolean {
      return !!this.$slots.button
    },
    textToDisplay(): string {
      return this.modelValue
        ? `&nbsp;&nbsp;<strong>${this.formattedDate}</strong>`
        : ''
    },
    hasSelectedDate(): boolean {
      return !!this.modelValue
    },
    buttonTitle(): string {
      if (this.formattedDate) {
        return `${this.buttonTitlePrefix}: ${this.formattedDate}`
      }

      return `No ${this.buttonTitlePrefix.toLowerCase()} date`
    },
  },

  methods: {
    toggleCalendar() {
      if (!this.isDisabled) {
        this.calendarVisible = !this.calendarVisible
      }
    },

    handlePick(date: Date) {
      // Always emit a timezone-agnostic date from here...
      const value = date ? dayjs(date).format('YYYY-MM-DD') : null
      if (value !== this.modelValue) {
        this.$emit('update:modelValue', value)
      }
      this.calendarVisible = false
    },

    handleClear() {
      if (this.emitEventOnClear) {
        this.$emit('clear-due-date-clicked')
      } else {
        this.$emit('update:modelValue', null)
      }
      this.handleClose()
    },

    handleClose() {
      this.calendarVisible = false
    },

    /**
     * Return string for formated date
     */
    getFormattedDate(value: string): string {
      return dayjs(value).isSame(dayjs(), 'year') ? 'MMM D' : 'MMM D ’YY'
    },
  },
})
</script>

<style lang="scss" scoped>
.due-date-picker {
  position: relative;
  display: inline-block;

  &__button-slot {
    cursor: pointer;
  }

  /* Button */
  &__selector {
    display: flex;
    align-items: center;
    max-height: 40px;
    padding: 3px 7px 3px 1px;
    background-color: var(--color-background-secondary);
    border: none;
    border: solid 1px $light-blue-grey-two;
    border-radius: 8px;
    @include lettering--p-sm;
    color: var(--color-text-primary);

    &--selected {
      color: var(--color-text-secondary);
    }

    strong {
      font-weight: 500;
    }

    &:hover {
      margin: 0;
      filter: brightness(var(--hover-brightness));
      border: solid 1px $light-blue-grey-two !important;
      transition: none !important;
    }
  }

  .ddi-caret-down {
    @include dropdown-caret-size-color;
    margin-left: 4px;
  }

  .ddi-calendar {
    top: -1px;
    margin-left: 6px;
  }

  /* The calendar - tablet and up, centered under the button */
  &__dropdown {
    position: absolute;
    top: 100%;
    left: 50%;
    z-index: 10;
    width: 299px;
    margin-top: 4px;
    overflow: hidden;
    background-color: var(--color-background-header);
    border: 1px solid #e4e7ed;
    border-radius: 4px;
    box-shadow: $--select-dropdown-shadow;
    transform: translateX(-50%);
  }

  /* The calendar - mobile */
  &__sheet {
    position: relative;
  }

  :deep(.el-picker-panel) {
    width: 100%;
    background-color: transparent;
    border: none;
    box-shadow: none;

    .el-picker-panel__content {
      max-width: 267px !important;
      padding: 0 !important;
      margin: 0 15px 6px;
    }

    button {
      outline: 0;
    }

    .el-month-table td,
    .el-year-table td {
      padding: 10px 3px;
    }
  }

  &__footer {
    display: flex;
    padding: 6px 15px;
    line-height: 30px;
    border-top: 1px solid #e4e7ed;

    .clear-button {
      padding: 1px 6px;
      font-size: 14px;
      font-weight: 500;
      color: $color--blue;
      cursor: pointer;
      background: transparent;
      border: none;
    }
  }
}
</style>
